import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActiveCustomer } from '../models/active-customer';
import { UserAuthService } from '../UserAuth.service';
import { CustomerService } from './customer.service';

/** One entry per `${CustomerId}|${TenantId}`: the same id can exist under several tenants. */
export function dedupeCompanies(list: readonly ActiveCustomer[]): ActiveCustomer[] {
  const seen = new Set<string>();
  return list.filter((c) => {
    const key = `${c.CustomerId}|${c.TenantId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Keep the list of companies this user may switch to CURRENT.
 *
 * THE DEFECT (confirmed by bulk-import, 2026-09-24). The list was fetched in one
 * place, /select-customer, which redirects away as soon as a company is active.
 * From then on the in-app switcher read a sessionStorage copy taken at sign-in,
 * so a company granted after sign-in stayed invisible until the storage was
 * cleared. A user with one company never even saw the switcher, because it only
 * renders when there is more than one.
 *
 * Refreshed when the user menu opens, and when the server says the active
 * company is not the caller's (403 customer_not_callers_company). Single-flight;
 * a failed refresh keeps the list already shown rather than emptying it.
 */
@Injectable({ providedIn: 'root' })
export class AvailableCompaniesService {
  private inFlight: Promise<ActiveCustomer[] | null> | null = null;

  constructor(
    private readonly auth: UserAuthService,
    private readonly customers: CustomerService
  ) {}

  refresh(): Promise<ActiveCustomer[] | null> {
    if (!this.inFlight) {
      this.inFlight = this.fetch().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private async fetch(): Promise<ActiveCustomer[] | null> {
    const objectId = this.auth.getObjectId();
    if (!objectId) {
      return null;
    }
    try {
      const list = dedupeCompanies(
        await firstValueFrom(this.customers.getCustomers(objectId, this.auth.CustomerTenantId))
      );
      this.auth.setAvailableCustomers(list);
      return list;
    } catch (err) {
      console.warn('AvailableCompanies: refresh failed; keeping the list already shown', err);
      return null;
    }
  }
}
